/**
 * Per-status product counts for one Meta/TikTok product set, using the same status
 * buckets as the shared Ads Manager filter (ADS_STATUS_FILTER_PARAMS), so a card's
 * counts always agree with what the filter shows. One pageSize-1 query per bucket,
 * read from pagination.total. A fixed set of hooks keeps hook order stable. Callers pass
 * `skip` until the counts are shown (the card fetches them only once expanded), so a page
 * of collapsed cards issues no count queries.
 */
import type { Publisher } from 'src/store/generated/leela-api-generated';
import { usePublisherEntitiesControllerListForCampaignQuery } from 'src/store/leela-api';
import { ADS_STATUS_FILTER_PARAMS, type AdsStatusFilter } from './statusFilterParams';

/** Buckets shown on a product set card, in display order (matches StatusFilterSelect). */
export const CATALOG_COUNT_BUCKETS: readonly AdsStatusFilter[] = ['ALL', 'LIVE', 'PENDING', 'PROCESSING', 'FAILED', 'REMOVED'];

export type CatalogProductCounts = Record<AdsStatusFilter, number | undefined>;

interface CatalogProductCountsArgs {
  campaignId: string;
  publisher: Publisher;
  productSetId?: string;
  /** Defer the count queries (e.g. until the card is expanded). */
  skip?: boolean;
}

export function useCatalogProductCounts ({ campaignId, productSetId, publisher, skip: deferred = false }: CatalogProductCountsArgs): CatalogProductCounts {
  const skip = deferred || productSetId == null;
  const base = { campaignId, page: 1, pageSize: 1, parentPublisherEntityId: productSetId, publisher, publisherEntityType: 'ASSET' as const };
  const all = usePublisherEntitiesControllerListForCampaignQuery({ ...base, ...ADS_STATUS_FILTER_PARAMS.ALL }, { skip });
  const live = usePublisherEntitiesControllerListForCampaignQuery({ ...base, ...ADS_STATUS_FILTER_PARAMS.LIVE }, { skip });
  const pending = usePublisherEntitiesControllerListForCampaignQuery({ ...base, ...ADS_STATUS_FILTER_PARAMS.PENDING }, { skip });
  const processing = usePublisherEntitiesControllerListForCampaignQuery({ ...base, ...ADS_STATUS_FILTER_PARAMS.PROCESSING }, { skip });
  const failed = usePublisherEntitiesControllerListForCampaignQuery({ ...base, ...ADS_STATUS_FILTER_PARAMS.FAILED }, { skip });
  const removed = usePublisherEntitiesControllerListForCampaignQuery({ ...base, ...ADS_STATUS_FILTER_PARAMS.REMOVED }, { skip });

  return {
    ALL: all.data?.meta.pagination.total,
    FAILED: failed.data?.meta.pagination.total,
    LIVE: live.data?.meta.pagination.total,
    PENDING: pending.data?.meta.pagination.total,
    PROCESSING: processing.data?.meta.pagination.total,
    REMOVED: removed.data?.meta.pagination.total,
  };
}
